// ═══════════════════════════════════════════════════════════════════════════
// PapaReportDashboard.js — analytics for Papa operations
// Backend: papa.php?action=report (extended with kpi/dailyTrend/contractorPerf/productSummary/...)
// Renders inside PapaDashboard.js Reports tab via <PapaReportDashboard embedded />
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

// ── Helpers ──────────────────────────────────────────────────────────────────
const TODAY       = new Date().toISOString().slice(0, 10);
const MONTH_START = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
const fmt   = (n, d = 2) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtN  = (n) => Number(n || 0).toLocaleString();
const fmtDate = (d) => { if (!d) return '—'; const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); };
const clr   = ['#1565c0','#059669','#d97706','#7c3aed','#d32f2f','#0891b2'];

// Yield color thresholds for Papa (similar to others: <80% red, 80-85% amber, >=85% green)
const yldBgClass = (y) => y >= 85 ? 'green' : y >= 80 ? 'amber' : 'red';

// ── SVG Bar Chart ──
const BarChart = ({ data = [], xKey, bars = [], colors = clr, height = 220, horizontal = false }) => {
  if (!data.length) return <div className="prd-chart-empty"><span>📊</span><span>No data for selected filters</span></div>;
  const W = 520, pL = horizontal ? 120 : 48, pB = 52, pT = 18, pR = 12;
  const cW = W - pL - pR, cH = height - pB - pT;
  const maxVal = Math.max(...data.flatMap(d => bars.map(b => parseFloat(d[b.key]) || 0)), 1);
  const gW = horizontal ? cH / data.length : cW / data.length;
  const bW = Math.max(6, Math.min(28, (gW / bars.length) - 6));
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        if (horizontal) {
          const xx = pL + cW * t;
          return <g key={t}>
            <line x1={xx} y1={pT} x2={xx} y2={pT + cH} stroke="#e2e6ea" strokeWidth="1" />
            <text x={xx} y={pT + cH + 14} textAnchor="middle" fontSize="9" fill="#9ca3af">{(maxVal * t).toFixed(0)}</text>
          </g>;
        }
        const yy = pT + cH * (1 - t);
        return <g key={t}>
          <line x1={pL} y1={yy} x2={W - pR} y2={yy} stroke="#e2e6ea" strokeWidth="1" />
          <text x={pL - 5} y={yy + 4} textAnchor="end" fontSize="9" fill="#9ca3af">{(maxVal * t).toFixed(0)}</text>
        </g>;
      })}
      {data.map((d, gi) => {
        if (horizontal) {
          const totalGap = bars.length * bW + (bars.length - 1) * 3;
          const yStart = pT + gi * gW + (gW - totalGap) / 2;
          return (
            <g key={gi}>
              {bars.map((bar, bi) => {
                const val = parseFloat(d[bar.key]) || 0;
                const bL = (val / maxVal) * cW;
                const y = yStart + bi * (bW + 3);
                return (
                  <g key={bi}>
                    <rect x={pL} y={y} width={Math.max(bL, 2)} height={bW} fill={colors[bi % colors.length]} rx="3" opacity="0.88">
                      <title>{d[xKey]} — {bar.label}: {val.toFixed(1)}</title>
                    </rect>
                    {bL > 30 && <text x={pL + bL - 5} y={y + bW / 2 + 3} textAnchor="end" fontSize="9" fill="#fff" fontWeight="600">{val > 999 ? (val/1000).toFixed(1)+'k' : val.toFixed(0)}</text>}
                  </g>
                );
              })}
              <text x={pL - 6} y={yStart + totalGap / 2 + 3} textAnchor="end" fontSize="10" fill="#374151">
                {String(d[xKey] || '').length > 16 ? String(d[xKey]).slice(0, 16) + '…' : d[xKey]}
              </text>
            </g>
          );
        }
        const midX = pL + gi * gW + gW / 2;
        return (
          <g key={gi}>
            {bars.map((bar, bi) => {
              const val = parseFloat(d[bar.key]) || 0;
              const bh = (val / maxVal) * cH;
              const totalGap = bars.length * bW + (bars.length - 1) * 3;
              const x = pL + gi * gW + (gW - totalGap) / 2 + bi * (bW + 3);
              const y = pT + cH - bh;
              return (
                <g key={bi}>
                  <rect x={x} y={y} width={bW} height={Math.max(bh, 2)} fill={colors[bi % colors.length]} rx="3" opacity="0.88">
                    <title>{d[xKey]} — {bar.label}: {val.toFixed(1)}</title>
                  </rect>
                  {bh > 16 && <text x={x + bW / 2} y={y + 11} textAnchor="middle" fontSize="8" fill="#fff" fontWeight="600">{val > 999 ? (val/1000).toFixed(1)+'k' : val.toFixed(0)}</text>}
                </g>
              );
            })}
            <text x={midX} y={height - 6} textAnchor="middle" fontSize="9" fill="#6b7280">
              {String(d[xKey] || '').length > 9 ? String(d[xKey]).slice(0, 9) + '…' : d[xKey]}
            </text>
          </g>
        );
      })}
      {bars.length > 1 && bars.map((b, bi) => (
        <g key={bi} transform={`translate(${pL + bi * 110}, ${pT - 8})`}>
          <rect x="0" y="-7" width="10" height="10" fill={colors[bi % colors.length]} rx="2" />
          <text x="14" y="2" fontSize="9" fill="#6b7280">{b.label}</text>
        </g>
      ))}
      <line x1={pL} y1={pT} x2={pL} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
      <line x1={pL} y1={pT + cH} x2={W - pR} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
    </svg>
  );
};

// ── SVG Line Chart ──
const LineChart = ({ data = [], xKey, lines = [], colors = clr, height = 200 }) => {
  if (!data.length) return <div className="prd-chart-empty"><span>📈</span><span>No trend data available</span></div>;
  const W = 520, pL = 48, pB = 42, pT = 18, pR = 12;
  const cW = W - pL - pR, cH = height - pB - pT;
  const allVals = data.flatMap(d => lines.map(l => parseFloat(d[l.key]) || 0));
  const maxV = Math.max(...allVals, 1);
  const X = (i) => pL + (data.length < 2 ? cW / 2 : (i / (data.length - 1)) * cW);
  const Y = (v) => pT + cH - (v / maxV) * cH;
  const step = Math.max(1, Math.ceil(data.length / 7));
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{ width: '100%', height: 'auto' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const yy = pT + cH * (1 - t);
        return <g key={t}>
          <line x1={pL} y1={yy} x2={W - pR} y2={yy} stroke="#e2e6ea" strokeWidth="1" />
          <text x={pL - 5} y={yy + 4} textAnchor="end" fontSize="9" fill="#9ca3af">{(maxV * t).toFixed(1)}</text>
        </g>;
      })}
      {lines.map((line, li) => {
        const pts = data.map((d, i) => `${X(i)},${Y(parseFloat(d[line.key]) || 0)}`).join(' ');
        return (
          <g key={li}>
            <polyline points={pts} fill="none" stroke={colors[li % colors.length]} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
            {data.map((d, i) => {
              const val = parseFloat(d[line.key]) || 0;
              return (
                <circle key={i} cx={X(i)} cy={Y(val)} r="3.5" fill={colors[li % colors.length]} stroke="#fff" strokeWidth="1.5">
                  <title>{fmtDate(d[xKey])}: {val.toFixed(2)}</title>
                </circle>
              );
            })}
          </g>
        );
      })}
      {data.map((d, i) => {
        if (i % step !== 0 && i !== data.length - 1) return null;
        return (
          <text key={i} x={X(i)} y={height - 5} textAnchor="middle" fontSize="9" fill="#6b7280">
            {String(d[xKey] || '').slice(5)}
          </text>
        );
      })}
      {lines.length > 1 && lines.map((l, li) => (
        <g key={li} transform={`translate(${pL + li * 110}, ${pT - 6})`}>
          <line x1="0" y1="0" x2="16" y2="0" stroke={colors[li % colors.length]} strokeWidth="2" />
          <circle cx="8" cy="0" r="3" fill={colors[li % colors.length]} />
          <text x="20" y="4" fontSize="9" fill="#6b7280">{l.label}</text>
        </g>
      ))}
      <line x1={pL} y1={pT} x2={pL} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
      <line x1={pL} y1={pT + cH} x2={W - pR} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
    </svg>
  );
};

// ── Inline CSS (scoped via prd- prefix to avoid Bravo/Romeo/Golf collision) ──
const CSS = `
.prd-wrap { padding: 0; }
.prd-section-pill { display:inline-flex; align-items:center; gap:6px; padding:4px 12px; background:rgba(217,119,6,.08); border-radius:20px; border:1px solid rgba(217,119,6,.2); margin-bottom:8px; }
.prd-section-pill span { font-size:11px; color:#854F0B; font-weight:700; letter-spacing:.3px; }
.prd-h1 { font-size:18px; font-weight:700; color:#111827; }
.prd-livesync { display:inline-flex; align-items:center; gap:4px; margin-left:10px; padding:2px 9px; border-radius:20px; background:rgba(5,150,105,.1); border:1px solid rgba(5,150,105,.2); font-size:10px; font-weight:600; color:#059669; vertical-align:middle; }
.prd-sub { font-size:12px; color:#6b7280; margin-bottom:14px; }

.prd-filter-bar { background:#fff; border:1px solid #e2e6ea; border-radius:10px; padding:14px; display:flex; gap:14px; align-items:flex-end; flex-wrap:wrap; margin-bottom:14px; }
.prd-ff { display:flex; flex-direction:column; gap:4px; }
.prd-ff label { font-size:10px; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:.4px; }
.prd-ff input, .prd-ff select { padding:7px 10px; border:1px solid #d1d5db; border-radius:6px; font-family:inherit; font-size:12px; background:#fff; min-width:120px; }
.prd-apply-btn { padding:8px 16px; border:none; border-radius:6px; background:linear-gradient(135deg,#d97706,#1565c0); color:#fff; font-size:12px; font-weight:700; cursor:pointer; }
.prd-reset-btn { padding:8px 14px; border:1px solid #d1d5db; border-radius:6px; background:#fff; color:#374151; font-size:12px; font-weight:700; cursor:pointer; }

.prd-preset-grp { display:inline-flex; gap:4px; padding:3px; background:#f8f9fa; border:1px solid #e2e6ea; border-radius:7px; }
.prd-preset-btn { padding:5px 11px; border-radius:5px; border:none; background:transparent; color:#6b7280; font-size:11px; font-weight:700; cursor:pointer; font-family:inherit; }
.prd-preset-btn.active { background:linear-gradient(135deg,#d97706,#1565c0); color:#fff; }

.prd-export-row { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; }
.prd-exp-btn { padding:7px 14px; border:none; border-radius:6px; font-size:11px; font-weight:700; cursor:pointer; font-family:inherit; }
.prd-exp-btn.full { background:linear-gradient(135deg,#7c3aed,#1565c0); color:#fff; }
.prd-exp-btn.con  { background:#dbeafe; color:#1565c0; border:1px solid #93c5fd; }
.prd-exp-btn.prod { background:#ecfdf5; color:#065f46; border:1px solid #6ee7b7; }
.prd-exp-btn.inc  { background:#fef3c7; color:#854F0B; border:1px solid #fbbf24; }

.prd-kpi-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(150px, 1fr)); gap:10px; margin-bottom:14px; }
.prd-kpi-card { background:#fff; border:1px solid #e2e6ea; border-left:4px solid; border-radius:10px; padding:11px 14px; display:flex; align-items:center; gap:9px; }
.prd-kpi-icon { font-size:22px; }
.prd-kpi-lbl { font-size:9px; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:.4px; }
.prd-kpi-val { font-size:18px; font-weight:700; font-family:'DM Mono',monospace; }
.prd-kpi-card.red    { border-left-color:#d32f2f; } .prd-kpi-card.red    .prd-kpi-val { color:#d32f2f; }
.prd-kpi-card.blue   { border-left-color:#1565c0; } .prd-kpi-card.blue   .prd-kpi-val { color:#1565c0; }
.prd-kpi-card.green  { border-left-color:#059669; } .prd-kpi-card.green  .prd-kpi-val { color:#059669; }
.prd-kpi-card.amber  { border-left-color:#d97706; } .prd-kpi-card.amber  .prd-kpi-val { color:#d97706; }
.prd-kpi-card.purple { border-left-color:#7c3aed; } .prd-kpi-card.purple .prd-kpi-val { color:#7c3aed; }

.prd-card { background:#fff; border:1px solid #e2e6ea; border-radius:10px; margin-bottom:14px; }
.prd-card-hdr { display:flex; align-items:center; padding:14px; gap:10px; border-bottom:1px solid #f1f5f9; }
.prd-card-title { font-size:14px; font-weight:700; color:#111827; }
.prd-pill { padding:3px 10px; border-radius:12px; font-size:11px; font-weight:700; }
.prd-pill.blue  { background:rgba(21,101,192,.08); border:1px solid rgba(21,101,192,.2); color:#1565c0; }
.prd-pill.green { background:#ecfdf5; border:1px solid #6ee7b7; color:#065f46; }
.prd-pill.amber { background:#fef3c7; border:1px solid #fbbf24; color:#92400e; }
.prd-pill.red   { background:#fee2e2; border:1px solid #fca5a5; color:#991b1b; }

.prd-charts-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:14px; }
@media (max-width:900px) { .prd-charts-row { grid-template-columns:1fr; } }
.prd-chart-wrap { padding:14px; min-height:240px; position:relative; max-width:640px; margin:0 auto; }
.prd-chart-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; color:#9ca3af; height:200px; font-size:12px; }
.prd-chart-empty span:first-child { font-size:36px; opacity:.4; }

.prd-tbl-wrap { padding:0 14px 14px; overflow-x:auto; }
.prd-tbl { width:100%; border-collapse:collapse; font-size:11px; }
.prd-tbl thead tr { background:#f8f9fa; border-bottom:1.5px solid #e2e6ea; }
.prd-tbl th { padding:10px 12px; font-weight:700; font-size:10px; color:#374151; text-transform:uppercase; letter-spacing:.3px; text-align:left; }
.prd-tbl th.r { text-align:right; }
.prd-tbl th.c { text-align:center; }
.prd-tbl tbody tr { border-bottom:1px solid #f1f5f9; }
.prd-tbl tbody tr:hover { background:#fafffd; }
.prd-tbl td { padding:9px 12px; }
.prd-tbl td.r { text-align:right; font-family:'DM Mono',monospace; }
.prd-tbl td.c { text-align:center; }
.prd-tbl tfoot tr { background:#f0f2f5; border-top:2px solid #e2e6ea; font-weight:700; }
.prd-tbl tfoot td { padding:10px 12px; }
`;

let cssInjected = false;
const ensureCSS = () => {
  if (cssInjected) return;
  const tag = document.createElement('style');
  tag.id = 'prd-styles';
  tag.textContent = CSS;
  document.head.appendChild(tag);
  cssInjected = true;
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const PapaReportDashboard = ({ embedded = false }) => {
  const { user } = usePPCAuth();
  const auth = useCallback(() => {
    const t = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${t}` } };
  }, []);

  // ── State ────────────────────────────────────────────────────────────────
  const [dateFrom,    setDateFrom]    = useState(MONTH_START);
  const [dateTo,      setDateTo]      = useState(TODAY);
  const [filterCont,  setFilterCont]  = useState('');
  const [filterShift, setFilterShift] = useState('');
  const [filterProd,  setFilterProd]  = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [applied, setApplied] = useState({ from: MONTH_START, to: TODAY, cont: '', shift: '', product: '', grade: '' });
  const [quickPreset, setQuickPreset] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState(null);

  useEffect(() => { ensureCSS(); }, []);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (a) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from: a.from, to: a.to, contractors: a.cont || 'all' });
      if (a.shift)   params.set('shift',   a.shift);
      if (a.product) params.set('product', a.product);
      if (a.grade)   params.set('grade',   a.grade);
      const res = await axios.get(`${PPC_API_URL}/papa.php?action=report&${params}`, auth());
      if (res.data?.success) setData(res.data);
      else { console.error('Papa report fetch returned non-success:', res.data); setData(null); }
    } catch (e) {
      console.error('Papa report fetch error:', e.response?.data || e.message);
      setData(null);
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { fetchData(applied); }, [applied, fetchData]);

  // ── Filter actions ───────────────────────────────────────────────────────
  const applyFilters = () => {
    setApplied({ from: dateFrom, to: dateTo, cont: filterCont, shift: filterShift, product: filterProd, grade: filterGrade });
  };
  const applyPreset = (preset) => {
    const today = new Date();
    let from, to;
    if (preset === 'daily') { from = to = today.toISOString().slice(0, 10); }
    else if (preset === 'weekly') {
      const day = today.getDay();
      const mon = new Date(today); mon.setDate(today.getDate() - ((day + 6) % 7));
      const sun = new Date(mon);   sun.setDate(mon.getDate() + 6);
      from = mon.toISOString().slice(0, 10); to = sun.toISOString().slice(0, 10);
    } else if (preset === 'monthly') {
      const y = today.getFullYear(), m = today.getMonth();
      from = new Date(y, m, 1).toISOString().slice(0, 10);
      to   = new Date(y, m + 1, 0).toISOString().slice(0, 10);
    } else return;
    setQuickPreset(preset);
    setDateFrom(from); setDateTo(to);
    setApplied({ from, to, cont: filterCont, shift: filterShift, product: filterProd, grade: filterGrade });
  };
  const resetFilters = () => {
    setDateFrom(MONTH_START); setDateTo(TODAY);
    setFilterCont(''); setFilterShift(''); setFilterProd(''); setFilterGrade('');
    setApplied({ from: MONTH_START, to: TODAY, cont: '', shift: '', product: '', grade: '' });
    setQuickPreset('monthly');
  };

  // ── Excel export (via SheetJS — already in their bundle) ────────────────
  const exportFull = () => {
    if (!data) return;
    const k = data.kpi || {};
    const inc = data.incentiveDistribution || {};
    const wb = XLSX.utils.book_new();
    const summary = [
      ['Papa Reports', `${applied.from} to ${applied.to}`],
      [],
      ['KPI', 'Value'],
      ['Total Contractors',  k.totalContractors || 0],
      ['Total Tokens',       k.totalTokens || 0],
      ['Total Associates',   k.totalAssociates || 0],
      ['Total HL Qty (KG)',  k.totalHlQty || 0],
      ['Total Output (KG)',  k.totalOutputQty || 0],
      ['Avg Yield %',        k.avgYield || 0],
      ['Avg Productivity',   k.avgProductivity || 0],
      ['Total Hours',        k.totalHours || 0],
      ['Total Entries',      k.totalEntries || 0],
      [],
      ['Incentive Summary', ''],
      ['Above 16 tokens',    inc.aboveStd || 0],
      ['Below standard',     inc.belowStd || 0],
      ['Total extras pool',  inc.totalExtras || 0],
      ['% Above Standard',   inc.pctAboveStd || 0],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
    if ((data.dailyTrend || []).length) {
      const th = [['Date', 'HL Qty', 'Output Qty', 'Tokens', 'Hours', 'Yield %', 'Productivity']];
      const tr = data.dailyTrend.map(t => [t.date, t.hl_qty, t.output_qty, t.tokens, t.hours, t.yield, t.productivity]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([...th, ...tr]), 'Daily Trend');
    }
    XLSX.writeFile(wb, `papa_full_${applied.from}_${applied.to}.xlsx`);
  };

  const exportContractors = () => {
    if (!data?.contractorPerf || !data.contractorPerf.length) return;
    const wb = XLSX.utils.book_new();
    const h = [['Contractor', 'Tokens', 'HL Qty', 'Output Qty', 'Yield %', 'Productivity (KG/hr)', 'Hours', 'Associates', 'Entries', '% of Total']];
    const r = data.contractorPerf.map(c => [c.contractor, c.tokens, c.hl_qty, c.output_qty, c.yield, c.productivity, c.hours, c.assoc, c.entries, c.share]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([...h, ...r]), 'Contractors');
    XLSX.writeFile(wb, `papa_contractors_${applied.from}_${applied.to}.xlsx`);
  };

  const exportProducts = () => {
    if (!data?.productSummary || !data.productSummary.length) return;
    const wb = XLSX.utils.book_new();
    const h = [['Product', 'Grade', 'HL Qty', 'Output Qty', 'Yield %', 'Productivity (KG/hr)', 'Hours', 'Entries']];
    const r = data.productSummary.map(p => [p.product, p.grade, p.hl_qty, p.output_qty, p.yield, p.productivity, p.hours, p.entries]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([...h, ...r]), 'Products');
    XLSX.writeFile(wb, `papa_products_${applied.from}_${applied.to}.xlsx`);
  };

  const exportEarners = () => {
    if (!data?.topEarners || !data.topEarners.length) return;
    const wb = XLSX.utils.book_new();
    const h = [['Employee Name', 'Employee ID', 'Contractor', 'Total Tokens', 'Extra Tokens', 'Days Worked', 'Last Date']];
    const r = data.topEarners.map(e => [e.emp_name, e.emp_id, e.contractor, e.total_tokens, e.extra_tokens, e.days_worked, e.last_date]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([...h, ...r]), 'Top Earners');
    XLSX.writeFile(wb, `papa_incentive_${applied.from}_${applied.to}.xlsx`);
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const kpi      = data?.kpi || {};
  const trend    = data?.dailyTrend || [];
  const cpf      = data?.contractorPerf || [];
  const prod     = data?.productSummary || [];
  const earners  = data?.topEarners || [];
  const inc      = data?.incentiveDistribution || {};
  const ctorList = data?.contractors || [];
  const prodList = data?.products || [];
  const gradeList = data?.grades || [];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="prd-wrap">
      {embedded && (
        <div>
          <div className="prd-section-pill"><span>📊 SECTION 2 — PAPA REPORTS</span></div>
          {/* MOVED: presets + exports now inline with title (was below filter bar) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '8px 0 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="prd-h1">Papa Reports</span>
              <span className="prd-livesync">● Live sync</span>
            </div>
            <div className="prd-preset-grp">
              {[
                { v:'daily',   l:'Daily',   i:'📅' },
                { v:'weekly',  l:'Weekly',  i:'📆' },
                { v:'monthly', l:'Monthly', i:'🗓️' },
              ].map(p => (
                <button key={p.v} type="button"
                  className={`prd-preset-btn${quickPreset === p.v ? ' active' : ''}`}
                  onClick={() => applyPreset(p.v)}>
                  {p.i} {p.l}
                </button>
              ))}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="prd-exp-btn full" onClick={exportFull}>📥 Full Export</button>
              <button className="prd-exp-btn con"  onClick={exportContractors}>👤 Contractor</button>
              <button className="prd-exp-btn prod" onClick={exportProducts}>📦 Product</button>
              <button className="prd-exp-btn inc"  onClick={exportEarners}>🪙 Incentive</button>
            </div>
          </div>
          <div className="prd-sub">Summary and analytics — {applied.from} to {applied.to}</div>
        </div>
      )}

      {/* ── Filter Bar (Quick preset moved up — see header) ── */}
      <div className="prd-filter-bar">
        <div className="prd-ff"><label>Date From</label><input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setQuickPreset(''); }} /></div>
        <div className="prd-ff"><label>Date To</label><input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setQuickPreset(''); }} /></div>
        <div className="prd-ff">
          <label>Contractor</label>
          <select value={filterCont} onChange={e => setFilterCont(e.target.value)}>
            <option value="">All Contractors</option>
            {ctorList.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="prd-ff">
          <label>Shift</label>
          <select value={filterShift} onChange={e => setFilterShift(e.target.value)}>
            <option value="">All Shifts</option>
            <option value="DAY">☀️ Day</option>
            <option value="NIGHT">🌙 Night</option>
          </select>
        </div>
        <div className="prd-ff">
          <label>Product</label>
          <select value={filterProd} onChange={e => setFilterProd(e.target.value)}>
            <option value="">All Products</option>
            {prodList.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="prd-ff">
          <label>Grade</label>
          <select value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
            <option value="">All Grades</option>
            {gradeList.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <button className="prd-apply-btn" onClick={applyFilters}>▶ Apply</button>
        <button className="prd-reset-btn" onClick={resetFilters}>↺ Reset</button>
      </div>

      {/* (MOVED: Export Row was here, now inline with header above) */}

      {/* ── KPI Cards (9) ── */}
      <div className="prd-kpi-grid">
        {[
          { i:'🏢', l:'Contractors',       v:fmtN(kpi.totalContractors), c:'red' },
          { i:'🪙', l:'Total Tokens',      v:fmtN(kpi.totalTokens),      c:'amber' },
          { i:'👥', l:'Total Associates',  v:fmtN(kpi.totalAssociates),  c:'purple' },
          { i:'⚖️', l:'HL Qty (KG)',      v:fmt(kpi.totalHlQty, 0),     c:'blue' },
          { i:'📦', l:'Output Qty (KG)',   v:fmt(kpi.totalOutputQty, 0), c:'amber' },
          { i:'📈', l:'Avg Yield %',       v:(kpi.avgYield||0)+'%',      c: yldBgClass(kpi.avgYield||0) },
          { i:'⚡', l:'Avg Productivity',  v:fmt(kpi.avgProductivity, 1) + ' KG/hr', c:'purple' },
          { i:'🎁', l:'Incentive Tokens',  v:'+' + fmtN(inc.totalExtras || 0), c:'amber' },
          { i:'🎯', l:'Above 16 Tokens',   v:fmtN(inc.aboveStd || 0),    c:'red' },
        ].map((k, i) => (
          <div key={i} className={`prd-kpi-card ${k.c}`}>
            <span className="prd-kpi-icon">{k.i}</span>
            <div>
              <div className="prd-kpi-lbl">{k.l}</div>
              <div className="prd-kpi-val">{loading ? '…' : k.v}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Smart Insights ── */}
      {data && (kpi.totalEntries || 0) > 0 && (() => {
        const topCtor = cpf[0];
        const sortedByProd = [...cpf].sort((a,b) => (b.productivity||0) - (a.productivity||0));
        const topProd     = sortedByProd[0];
        const topProduct  = prod[0];
        const sortedByYield = [...prod].sort((a,b) => (a.yield||0) - (b.yield||0));
        const lowestYield = sortedByYield[0];
        const topExtraCtor = (data.contractorExtras || [])[0];

        const tiles = [];
        if (topCtor) tiles.push({ bg:'#ecfdf5', bd:'#059669', icon:'🏆', text:<><strong>Top Contractor:</strong> {topCtor.contractor} — {fmt(topCtor.output_qty, 0)} KG output</> });
        if (topProd && (topProd.productivity || 0) > 0) tiles.push({ bg:'#dbeafe', bd:'#1565c0', icon:'⚡', text:<><strong>Top Productivity:</strong> {topProd.contractor} — {fmt(topProd.productivity, 1)} KG/hr</> });
        if (topProduct) tiles.push({ bg:'#fef3c7', bd:'#d97706', icon:'📦', text:<><strong>Top Product:</strong> {topProduct.product} {topProduct.grade ? `Grade ${topProduct.grade}` : ''} — {fmt(topProduct.output_qty, 0)} KG</> });
        if (topExtraCtor) tiles.push({ bg:'#ede9fe', bd:'#7c3aed', icon:'🪙', text:<><strong>Top Incentives:</strong> {topExtraCtor.contractor} — +{fmtN(topExtraCtor.extras)} tokens</> });
        if (lowestYield && (lowestYield.yield || 0) < 85) tiles.push({ bg:'#fee2e2', bd:'#dc2626', icon:'⚠', text:<><strong>Lowest Yield:</strong> {lowestYield.product} {lowestYield.grade ? `Grade ${lowestYield.grade}` : ''} — {(lowestYield.yield||0).toFixed(1)}%</> });
        tiles.push({
          bg:'#f0fdf4', bd:'#16a34a', icon:'✓',
          text:<><strong>Activity:</strong> {kpi.totalEntries} entries · {cpf.length} contractor{cpf.length !== 1 ? 's' : ''} · {inc.totalEmps || 0} employees</>,
        });

        return (
          <div className="prd-card">
            <div className="prd-card-hdr">
              <span className="prd-card-title">💡 Smart Insights</span>
              <span className="prd-pill blue" style={{ marginLeft: 10 }}>auto-generated</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:8, padding:14 }}>
              {tiles.map((t, i) => (
                <div key={i} style={{ padding:'10px 12px', background:t.bg, borderLeft:`3px solid ${t.bd}`, borderRadius:6, fontSize:11, color:'#1f2937', display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:16 }}>{t.icon}</span>
                  <span style={{ flex:1 }}>{t.text}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Charts row: Production Trend + Contractor Performance ── */}
      <div className="prd-charts-row">
        <div className="prd-card">
          <div className="prd-card-hdr"><span className="prd-card-title">📈 Production Trend (HL vs Output)</span></div>
          <div className="prd-chart-wrap">
            {trend.length === 0 ? (
              <div className="prd-chart-empty"><span>📈</span><span>No data available for selected timeframe.</span></div>
            ) : (
              <LineChart data={trend} xKey="date"
                lines={[
                  { key:'hl_qty',     label:'HL Qty (KG)' },
                  { key:'output_qty', label:'Output Qty (KG)' },
                ]}
                colors={['#1565c0','#d97706']} height={200} />
            )}
          </div>
        </div>

        <div className="prd-card">
          <div className="prd-card-hdr"><span className="prd-card-title">🏆 Contractor Performance (Top 10)</span></div>
          <div className="prd-chart-wrap">
            {cpf.length === 0 ? (
              <div className="prd-chart-empty"><span>🏆</span><span>No contractor data available.</span></div>
            ) : (
              <BarChart data={cpf.slice(0, 10)} xKey="contractor" horizontal
                bars={[
                  { key:'hl_qty',     label:'HL Qty' },
                  { key:'output_qty', label:'Output Qty' },
                ]}
                colors={['#1565c0','#d97706']} height={Math.max(180, cpf.slice(0, 10).length * 28)} />
            )}
          </div>
        </div>
      </div>

      {/* ── Contractor Summary table ── */}
      <div className="prd-card">
        <div className="prd-card-hdr">
          <span className="prd-card-title">📋 Contractor Summary</span>
          <span className="prd-pill blue" style={{ marginLeft: 10 }}>{cpf.length} contractor{cpf.length !== 1 ? 's' : ''}</span>
          <button className="prd-exp-btn con" onClick={exportContractors} style={{ marginLeft: 'auto', padding: '4px 10px' }}>📥 Export</button>
        </div>
        <div className="prd-tbl-wrap">
          {cpf.length === 0 ? (
            <div className="prd-chart-empty" style={{ height: 120 }}>
              <span>📋</span><span>No contractor data available.</span>
            </div>
          ) : (
            <table className="prd-tbl">
              <thead>
                <tr>
                  <th className="c">#</th>
                  <th>Contractor</th>
                  <th className="r">Tokens</th>
                  <th className="r">HL Qty (KG)</th>
                  <th className="r">Output Qty (KG)</th>
                  <th className="r">Yield %</th>
                  <th className="r">Productivity</th>
                  <th className="r">Hours</th>
                  <th className="r">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {cpf.map((c, i) => (
                  <tr key={c.contractor}>
                    <td className="c" style={{ color: '#9ca3af', fontFamily: "'DM Mono',monospace" }}>{i + 1}</td>
                    <td style={{ fontWeight: 600, color: '#1565c0' }}>{c.contractor}</td>
                    <td className="r" style={{ color: '#d97706' }}>{fmtN(c.tokens)}</td>
                    <td className="r" style={{ fontWeight: 700, color: '#1565c0' }}>{fmt(c.hl_qty, 0)}</td>
                    <td className="r" style={{ color: '#d97706', fontWeight: 700 }}>{fmt(c.output_qty, 0)}</td>
                    <td className="r"><span className={`prd-pill ${yldBgClass(c.yield)}`}>{c.yield.toFixed(1)}%</span></td>
                    <td className="r" style={{ color: '#7c3aed', fontWeight: 700 }}>{fmt(c.productivity, 1)} KG/hr</td>
                    <td className="r" style={{ color: '#6b7280' }}>{fmt(c.hours, 1)}</td>
                    <td className="r">{c.share.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>TOTAL ({cpf.length} contractors)</td>
                  <td className="r">{fmtN(cpf.reduce((s, c) => s + (c.tokens || 0), 0))}</td>
                  <td className="r" style={{ color: '#1565c0' }}>{fmt(cpf.reduce((s, c) => s + (c.hl_qty || 0), 0), 0)}</td>
                  <td className="r" style={{ color: '#d97706' }}>{fmt(cpf.reduce((s, c) => s + (c.output_qty || 0), 0), 0)}</td>
                  <td className="r">—</td>
                  <td className="r">—</td>
                  <td className="r">{fmt(cpf.reduce((s, c) => s + (c.hours || 0), 0), 1)}</td>
                  <td className="r">100%</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* ── Product Summary table ── */}
      <div className="prd-card">
        <div className="prd-card-hdr">
          <span className="prd-card-title">📦 Product Summary</span>
          <span className="prd-pill amber" style={{ marginLeft: 10 }}>{prod.length} product/grade combinations</span>
          <button className="prd-exp-btn prod" onClick={exportProducts} style={{ marginLeft: 'auto', padding: '4px 10px' }}>📥 Export</button>
        </div>
        <div className="prd-tbl-wrap">
          {prod.length === 0 ? (
            <div className="prd-chart-empty" style={{ height: 120 }}>
              <span>📦</span><span>No product data available.</span>
            </div>
          ) : (
            <table className="prd-tbl">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Grade</th>
                  <th className="r">HL Qty (KG)</th>
                  <th className="r">Output Qty (KG)</th>
                  <th className="r">Yield %</th>
                  <th className="r">Productivity</th>
                  <th className="r">Hours</th>
                  <th className="r">Entries</th>
                </tr>
              </thead>
              <tbody>
                {prod.map((p, i) => (
                  <tr key={`${p.product}|${p.grade}|${i}`}>
                    <td style={{ fontWeight: 600, color: '#1565c0' }}>{p.product}</td>
                    <td style={{ color: '#6b7280' }}>{p.grade || '—'}</td>
                    <td className="r" style={{ fontWeight: 700, color: '#1565c0' }}>{fmt(p.hl_qty, 0)}</td>
                    <td className="r" style={{ color: '#d97706', fontWeight: 700 }}>{fmt(p.output_qty, 0)}</td>
                    <td className="r"><span className={`prd-pill ${yldBgClass(p.yield)}`}>{p.yield.toFixed(1)}%</span></td>
                    <td className="r" style={{ color: '#7c3aed' }}>{fmt(p.productivity, 1)} KG/hr</td>
                    <td className="r" style={{ color: '#6b7280' }}>{fmt(p.hours, 1)}</td>
                    <td className="r">{p.entries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Employee Incentive table (Top Earners above 16 tokens) ── */}
      <div className="prd-card">
        <div className="prd-card-hdr">
          <span className="prd-card-title">🎁 Top Earners (Above 16 tokens)</span>
          <span className="prd-pill green" style={{ marginLeft: 10 }}>{earners.length} earner{earners.length !== 1 ? 's' : ''}</span>
          <button className="prd-exp-btn inc" onClick={exportEarners} style={{ marginLeft: 'auto', padding: '4px 10px' }}>📥 Export</button>
        </div>
        <div className="prd-tbl-wrap">
          {earners.length === 0 ? (
            <div className="prd-chart-empty" style={{ height: 120 }}>
              <span>🎁</span><span>No incentive earners in selected timeframe.</span>
            </div>
          ) : (
            <table className="prd-tbl">
              <thead>
                <tr>
                  <th className="c">#</th>
                  <th>Employee Name</th>
                  <th>Employee ID</th>
                  <th>Contractor</th>
                  <th className="r">Total Tokens</th>
                  <th className="r">Extra Tokens</th>
                  <th className="r">Days</th>
                </tr>
              </thead>
              <tbody>
                {earners.map((e, i) => (
                  <tr key={`${e.emp_id}-${i}`}>
                    <td className="c" style={{ color: '#9ca3af' }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{e.emp_name || 'Unknown'}</td>
                    <td style={{ fontFamily: "'DM Mono',monospace", color: '#6b7280', fontSize: 11 }}>{e.emp_id}</td>
                    <td style={{ color: '#1565c0' }}>{e.contractor}</td>
                    <td className="r">{fmtN(e.total_tokens)}</td>
                    <td className="r" style={{ fontWeight: 700, color: '#d97706' }}>+{fmtN(e.extra_tokens)}</td>
                    <td className="r">{e.days_worked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!loading && data && (kpi.totalEntries || 0) === 0 && (
        <div className="prd-card">
          <div className="prd-chart-empty" style={{ height: 180 }}>
            <span>📭</span>
            <span>No data available for selected timeframe.</span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Try a wider date range or check Papa entries</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PapaReportDashboard;
